import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { applyAuthEmailLanguage, getEmailActionSettings, sendVerificationEmail } from '../utils/authEmailSettings';
import { sendPasswordResetViaApi } from '../services/authApi';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verificationSession, setVerificationSession] = useState(false);
  const signupInProgressRef = useRef(false);

  const signup = async (email, password, additionalData = {}) => {
    signupInProgressRef.current = true;

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (additionalData.name) {
        try {
          await updateProfile(user, {
            displayName: additionalData.name,
          });
        } catch (err) {
          console.error('Erro ao atualizar perfil:', err);
        }
      }

      try {
        await setDoc(doc(db, 'users', user.uid), {
          name: additionalData.name || '',
          email: user.email,
          phone: additionalData.phone || '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      } catch (err) {
        console.error('Erro ao salvar usuário no Firestore:', err);
      }

      const idToken = await user.getIdToken();
      const emailSent = await sendVerificationEmail(user, idToken);

      try {
        await signOut(auth);
      } catch (err) {
        console.error('Erro ao deslogar após cadastro:', err);
      }

      return { email: user.email, emailSent };
    } finally {
      signupInProgressRef.current = false;
      setVerificationSession(false);
    }
  };

  const resendVerificationEmail = async () => {
    if (!auth.currentUser) {
      throw new Error('Usuário não autenticado');
    }
    const idToken = await auth.currentUser.getIdToken();
    const sent = await sendVerificationEmail(auth.currentUser, idToken);
    if (!sent) {
      throw new Error('Não foi possível enviar o email de verificação. Tente novamente em instantes.');
    }
  };

  const login = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    setVerificationSession(!result.user.emailVerified);
    return result;
  };

  const logout = () => {
    setVerificationSession(false);
    return signOut(auth);
  };

  const resetPassword = async (email) => {
    const trimmedEmail = email.trim();

    try {
      await sendPasswordResetViaApi(trimmedEmail);
      return;
    } catch (apiErr) {
      console.warn('Falha ao enviar reset via Resend, tentando Firebase:', apiErr);
    }

    applyAuthEmailLanguage();
    try {
      await sendPasswordResetEmail(auth, trimmedEmail, getEmailActionSettings());
    } catch (err) {
      console.warn('Falha ao enviar reset com URL customizada, tentando padrão:', err);
      await sendPasswordResetEmail(auth, trimmedEmail);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (loading || !currentUser?.emailVerified) return;
    setVerificationSession(false);
  }, [loading, currentUser?.emailVerified]);

  useEffect(() => {
    if (loading || verificationSession || signupInProgressRef.current) return;
    if (currentUser && !currentUser.emailVerified) {
      signOut(auth);
    }
  }, [loading, currentUser, verificationSession]);

  const isVerifiedUser = Boolean(currentUser?.emailVerified);

  const value = {
    currentUser,
    isVerifiedUser,
    verificationSession,
    setVerificationSession,
    signup,
    login,
    logout,
    resetPassword,
    resendVerificationEmail,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
